/**
 * The model's unparsed reply for one page.
 *
 * Fetched only when opened: it is large, and it is only wanted when someone
 * is debugging a specific failure (§7.3).
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, api, keys } from '~/lib/api.ts'
import { Button, Skeleton } from '~/components/ui/primitives.tsx'
import { Dialog } from '~/components/ui/overlay.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { useT } from '~/i18n/index.tsx'

type Raw = { rawText: string | null; model: string | null; promptVersion: string | null }

export function RawReplyDialog({
  open, onClose, documentId, pageNo,
}: { open: boolean; onClose: () => void; documentId: number; pageNo: number }) {
  const { t } = useT()
  const [copied, setCopied] = useState(false)

  const { data, error } = useQuery({
    queryKey: keys.documentRaw(documentId, pageNo),
    queryFn: () => api<Raw>(`/api/documents/${documentId}/pages/${pageNo}/raw`),
    enabled: open,
  })

  const copy = async () => {
    if (!data?.rawText) return
    try {
      await navigator.clipboard.writeText(data.rawText)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* a clipboard the browser refuses is not an error worth raising */ }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={900}
      closeLabel={t('action.close')}
      title={
        <span className="flex items-baseline gap-2">
          {t('viewer.raw')}
          <span className="text-[length:var(--text-micro)] font-normal text-[var(--text-faint)]">
            {t('viewer.page', { n: pageNo })}
            {data?.promptVersion && ` · ${data.promptVersion}`}
            {data?.model && ` · ${data.model}`}
          </span>
        </span>
      }
      footer={
        <Button size="sm" onClick={() => void copy()} disabled={!data?.rawText}>
          {copied ? t('action.copied') : t('action.copy')}
        </Button>
      }
    >
      {error ? (
        <ErrorState
          title={t('common.error')}
          detail={(error as ApiError).message}
          requestId={(error as ApiError).requestId}
        />
      ) : !data ? (
        <Skeleton className="h-64 w-full" />
      ) : data.rawText === null ? (
        <EmptyState title={t('empty.generic')} compact>
          {t('common.notRecorded')}
        </EmptyState>
      ) : (
        <pre
          dir="ltr"
          className="overflow-x-auto overscroll-contain whitespace-pre-wrap break-words font-[family-name:var(--font-mono)] text-[length:var(--text-micro)] leading-[var(--text-tiny--line-height)] text-[var(--text)]"
        >
          {data.rawText}
        </pre>
      )}
    </Dialog>
  )
}
